/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Eye, EyeOff, FileSearch, Lock, PenLine, ShieldCheck, Upload } from 'lucide-react';
import { KepPanel } from './KepPanel';
import { SignRequestPanel } from './SignRequest';
import { FormEvent, useRef, useState } from 'react';
import { api, download, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDateTime, fmtSize } from '@/lib/format';
import { useDebounced, useDict } from '@/lib/hooks';
import { Badge, Button, Empty, ErrorText, Field, Input, Modal, Pager, Select, Spinner } from './ui';

/** Документы с поиском по тексту, загрузкой и версиями. filter: partyId / matterId / contractId. */
export function DocumentsTable({ filter }: { filter: Record<string, string | undefined> }) {
  const { can, hasModule } = useAuth();
  const qc = useQueryClient();
  const [signFor, setSignFor] = useState<any>(null);
  const [kepFor, setKepFor] = useState<any>(null);
  const vis = useMutation({ mutationFn: (d: any) => api.post(`/portal-admin/documents/${d.id}/visible`, { visible: !d.portalVisible }), onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }) });
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [versionFor, setVersionFor] = useState<any>(null);
  const [viewing, setViewing] = useState<any>(null);
  const dq = useDebounced(q);
  const { data, isLoading } = useQuery({ queryKey: ['documents', filter, dq, page], queryFn: () => api.get(`/documents${qs({ ...filter, q: dq, page, pageSize: 50 })}`),
    refetchInterval: (q) => ((q.state.data as any)?.items?.some((d: any) => d.ocrStatus === 'PENDING' || d.ocrStatus === 'RUNNING') ? 4000 : false) });
  const done = () => qc.invalidateQueries({ queryKey: ['documents'] });
  return (
    <div className="card">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 p-3">
        <Input className="max-w-sm" placeholder="Поиск по названию и тексту" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <div className="flex-1" />
        {can('document.write') && <Button size="sm" onClick={() => setUploading(true)}><Upload size={14} /> Загрузить</Button>}
      </div>
      {isLoading ? <Spinner /> : !data?.items.length ? <Empty>Документов нет</Empty> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="bg-slate-50"><tr><th className="th">Документ</th><th className="th">Вид</th><th className="th">Текст</th><th className="th">Версия</th><th className="th">Изменён</th><th className="th"></th></tr></thead>
          <tbody>{data.items.map((d: any) => (
            <tr key={d.id} className="border-t border-slate-100">
              <td className="td"><div className="flex items-center gap-1.5">{d.confidential && <Lock size={12} className="text-amber-600" />}{d.title}</div>
                <div className="text-xs text-slate-500">{d.fileName} · {fmtSize(d.size)}</div>
                {d.tags.length > 0 && <div className="mt-0.5 space-x-1">{d.tags.map((t: string) => <Badge key={t} tone="blue">{t}</Badge>)}</div>}</td>
              <td className="td">{d.kind ?? '-'}</td>
              <td className="td"><OcrBadge d={d} /></td>
              <td className="td tabular-nums">v{d.version}</td>
              <td className="td text-xs">{fmtDateTime(d.updatedAt)}</td>
              <td className="td whitespace-nowrap text-right">
                <Button size="sm" variant="ghost" onClick={() => setViewing(d)} aria-label="Текст и реквизиты" title="Текст и реквизиты"><FileSearch size={14} /></Button>
                <Button size="sm" variant="ghost" onClick={() => download(`/documents/${d.id}/download`)} aria-label="Скачать"><Download size={14} /></Button>
                {can('document.write') && <Button size="sm" variant="ghost" onClick={() => setVersionFor(d)}>Новая версия</Button>}
                {can('document.write') && hasModule('M18') && !d.confidential && <Button size="sm" variant="ghost" onClick={() => vis.mutate(d)} title={d.portalVisible ? 'Виден в кабинете клиента. Нажмите, чтобы скрыть' : 'Показать в кабинете клиента'}>{d.portalVisible ? <Eye size={14} className="text-brand-600" /> : <EyeOff size={14} />}</Button>}
                {can('document.write') && hasModule('M13') && <Button size="sm" variant="ghost" onClick={() => setKepFor(d)} title="Подписать КЭП (КриптоПро)"><ShieldCheck size={14} className={d.tags?.includes('подписан КЭП') ? 'text-green-600' : ''} /></Button>}
                {can('document.write') && hasModule('M39') && <Button size="sm" variant="ghost" onClick={() => setSignFor(d)} title="Отправить клиенту на подпись"><PenLine size={14} /></Button>}
              </td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {data && <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />}
      <UploadModal open={uploading} onClose={() => setUploading(false)} filter={filter} onDone={done} />
      <Modal title={`КЭП: ${kepFor?.title ?? ''}`} open={!!kepFor} onClose={() => setKepFor(null)} wide>{kepFor && <KepPanel doc={kepFor} />}</Modal>
      <Modal title={`Подписание: ${signFor?.title ?? ''}`} open={!!signFor} onClose={() => setSignFor(null)} wide>{signFor && <SignRequestPanel doc={signFor} />}</Modal>
      <Modal title={viewing?.title ?? ''} open={!!viewing} onClose={() => setViewing(null)} wide>{viewing && <DocumentText id={viewing.id} />}</Modal>
      <Modal title={`Новая версия: ${versionFor?.title ?? ''}`} open={!!versionFor} onClose={() => setVersionFor(null)}>
        {versionFor && <VersionForm id={versionFor.id} onDone={() => { setVersionFor(null); done(); }} />}
      </Modal>
    </div>
  );
}

function UploadModal({ open, onClose, filter, onDone }: { open: boolean; onClose: () => void; filter: Record<string, string | undefined>; onDone: () => void }) {
  const { data: kinds } = useDict('document_kind');
  const file = useRef<HTMLInputElement>(null);
  const [f, setF] = useState({ title: '', kind: '', tags: '', confidential: false });
  const up = useMutation({
    mutationFn: async () => {
      const files = Array.from(file.current?.files ?? []);
      for (const x of files) {
        const fd = new FormData();
        fd.append('file', x);
        if (files.length === 1 && f.title) fd.append('title', f.title);
        if (f.kind) fd.append('kind', f.kind);
        if (f.tags) fd.append('tags', f.tags);
        fd.append('confidential', String(f.confidential));
        for (const [k, v] of Object.entries(filter)) if (v) fd.append(k, v);
        await api.post('/documents', fd);
      }
    },
    onSuccess: () => { onDone(); onClose(); setF({ title: '', kind: '', tags: '', confidential: false }); },
  });
  const submit = (e: FormEvent) => { e.preventDefault(); up.mutate(); };
  return (
    <Modal title="Загрузка документов" open={open} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <input ref={file} type="file" multiple required />
        <Field label="Название" hint="Если пусто - по имени файла"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Вид"><Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} placeholder="Не указан" options={(kinds ?? []).map((k) => ({ value: k.name, label: k.name }))} /></Field>
          <Field label="Теги"><Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} placeholder="через запятую" /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.confidential} onChange={(e) => setF({ ...f, confidential: e.target.checked })} /> Конфиденциально</label>
        <ErrorText error={up.error} />
        <div className="flex justify-end"><Button type="submit" disabled={up.isPending}>{up.isPending ? 'Загрузка...' : 'Загрузить'}</Button></div>
      </form>
    </Modal>
  );
}

function VersionForm({ id, onDone }: { id: string; onDone: () => void }) {
  const file = useRef<HTMLInputElement>(null);
  const up = useMutation({
    mutationFn: () => { const fd = new FormData(); fd.append('file', file.current!.files![0]); return api.post(`/documents/${id}/versions`, fd); },
    onSuccess: onDone,
  });
  return (
    <div className="space-y-3">
      <input ref={file} type="file" />
      <ErrorText error={up.error} />
      <div className="flex justify-end"><Button onClick={() => up.mutate()} disabled={up.isPending}>Загрузить версию</Button></div>
    </div>
  );
}

const OCR_METHOD: Record<string, string> = { 'text-layer': 'текст из PDF', tesseract: 'распознан', gigachat: 'распознан GigaChat', docx: 'текст из Word', text: 'текст', gigaam: 'расшифровка' };
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export function OcrBadge({ d }: { d: any }) {
  if (d.ocrStatus === 'PENDING' || d.ocrStatus === 'RUNNING') return <Badge tone="amber">распознаётся...</Badge>;
  if (d.ocrStatus === 'FAILED') return <Badge tone="red">ошибка</Badge>;
  if (d.ocrStatus === 'SKIPPED') return <span className="text-xs text-slate-400">нет текста</span>;
  if (d.ocrStatus === 'DONE') return <Badge tone={d.ocrConfidence != null && d.ocrConfidence < 70 ? 'amber' : 'green'}>{OCR_METHOD[d.ocrMethod] ?? d.ocrMethod}{d.ocrConfidence != null ? ` ${d.ocrConfidence}%` : ''}{d.ocrMethod === 'gigaam' && d.ocrPages ? ` ${mmss(d.ocrPages)}` : ''}</Badge>;
  return <span className="text-xs text-slate-400">-</span>;
}

/** Распознанный текст документа, повторное распознавание и извлечение реквизитов */
export function DocumentText({ id }: { id: string }) {
  const { can, hasModule } = useAuth();
  const qc = useQueryClient();
  const { data: d } = useQuery({ queryKey: ['document', id], queryFn: () => api.get(`/documents/${id}`), refetchInterval: (q) => (['PENDING', 'RUNNING'].includes((q.state.data as any)?.ocrStatus) ? 3000 : false) });
  const { data: t } = useQuery({ enabled: d?.ocrStatus === 'DONE' || !!d?.hasText, queryKey: ['document-text', id, d?.ocrAt], queryFn: () => api.get(`/documents/${id}/text`) });
  const inv = () => { qc.invalidateQueries({ queryKey: ['document', id] }); qc.invalidateQueries({ queryKey: ['document-text', id] }); qc.invalidateQueries({ queryKey: ['documents'] }); };
  const rerun = useMutation({ mutationFn: (engine: string) => api.post(`/documents/${id}/recognize${engine === 'gigachat' ? '?engine=gigachat' : ''}`), onSuccess: inv });
  const extract = useMutation({ mutationFn: () => api.post(`/documents/${id}/extract`), onSuccess: inv });
  const summary = useMutation({ mutationFn: () => api.post(`/documents/${id}/summary`), onSuccess: inv });
  const [created, setCreated] = useState(0);
  const mkTasks = useMutation({
    mutationFn: async (list: any[]) => {
      for (const t of list) {
        const due = t.due && /^\d{2}\.\d{2}\.\d{4}$/.test(t.due) ? new Date(`${t.due.split('.').reverse().join('-')}T18:00:00+03:00`).toISOString() : null;
        await api.post('/tasks', { title: t.title, description: t.who ? `Исполнитель по разговору: ${t.who}` : null, dueAt: due, matterId: d?.matterId ?? null, partyId: d?.partyId ?? null });
      }
      return list.length;
    },
    onSuccess: (n) => { setCreated(n); qc.invalidateQueries({ queryKey: ['tasks'] }); },
  });
  if (!d) return <Spinner />;
  const x: any = summary.data ?? extract.data ?? d.extracted;
  const isAudio = d.ocrMethod === 'gigaam';
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <OcrBadge d={d} />{d.ocrPages ? <span className="text-xs text-slate-500">страниц: {d.ocrPages}</span> : null}
        {d.ocrError && <span className="text-xs text-red-600">{d.ocrError}</span>}
        <div className="flex-1" />
        {can('document.write') && !isAudio && <>
          <Button size="sm" variant="secondary" disabled={rerun.isPending} onClick={() => rerun.mutate('auto')}>Распознать заново</Button>
          <Button size="sm" variant={d.lowConfidence ? 'primary' : 'secondary'} disabled={rerun.isPending} onClick={() => rerun.mutate('gigachat')}
            title="Для плохих фото и рукописных пометок. Изображение отправляется в облако Сбера">{rerun.isPending ? 'GigaChat читает...' : 'Распознать через GigaChat'}</Button>
        </>}
        {isAudio ? hasModule('M30') && <Button size="sm" disabled={summary.isPending || !d.hasText} onClick={() => summary.mutate()}>{summary.isPending ? 'Готовлю...' : 'Резюме и задачи'}</Button>
          : hasModule('M29') && <Button size="sm" disabled={extract.isPending || !d.hasText} onClick={() => extract.mutate()}>{extract.isPending ? 'Извлекаю...' : 'Извлечь реквизиты'}</Button>}
      </div>
      {d.lowConfidence && <div className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">Качество распознавания низкое (фото, муар, рукопись). Попробуйте «Распознать через GigaChat».</div>}
      <ErrorText error={rerun.error ?? extract.error} />
      {x?.kind === 'call' && (
        <div className="rounded-md border border-slate-200 p-3 text-sm">
          <div className="mb-1 font-medium">Резюме разговора</div>
          <div className="text-slate-700">{x.summary}</div>
          {x.participants?.length > 0 && <div className="mt-1 text-xs text-slate-500">Участники: {x.participants.join(', ')}</div>}
          {x.risks?.length > 0 && <div className="mt-2 text-amber-800">Внимание: {x.risks.join('; ')}</div>}
          {x.tasks?.length > 0 && <div className="mt-2">
            <div className="font-medium">Задачи</div>
            <ul className="list-disc pl-5">{x.tasks.map((t: any, i: number) => <li key={i}>{t.title}{t.due ? ` (до ${t.due})` : ''}{t.who ? ` - ${t.who}` : ''}</li>)}</ul>
            {can('task.write') && (created ? <div className="mt-1 text-green-700">Создано задач: {created}</div>
              : <Button size="sm" className="mt-2" disabled={mkTasks.isPending} onClick={() => mkTasks.mutate(x.tasks)}>Создать задачи{d.matterId ? ' по делу' : ''}</Button>)}
          </div>}
          <div className="mt-1 text-[11px] text-slate-400">Подготовлено ИИ по расшифровке, проверьте</div>
        </div>
      )}
      {x && !x.raw && x.kind !== 'call' && (
        <div className="rounded-md border border-slate-200 p-3 text-sm">
          <div className="mb-1 font-medium">{x.docType ?? 'Документ'}{x.number ? ` № ${x.number}` : ''}{x.date ? ` от ${x.date}` : ''}</div>
          {x.caseNumber && <div>Дело: <b>{x.caseNumber}</b>{x.court ? `, ${x.court}` : ''}</div>}
          {x.parties?.length > 0 && <div className="mt-1">Стороны: {x.parties.map((p: any) => `${p.name}${p.role ? ` (${p.role})` : ''}${p.inn ? `, ИНН ${p.inn}` : ''}`).join('; ')}</div>}
          {x.amounts?.length > 0 && <div className="mt-1">Суммы: {x.amounts.map((a: any) => `${a.value} - ${a.meaning}`).join('; ')}</div>}
          {x.deadlines?.length > 0 && <div className="mt-1">Сроки: {x.deadlines.map((a: any) => `${a.date} - ${a.meaning}`).join('; ')}</div>}
          {x.summary && <div className="mt-2 text-slate-600">{x.summary}</div>}
          <div className="mt-1 text-[11px] text-slate-400">Извлечено ИИ, проверьте по документу</div>
        </div>
      )}
      {x?.raw && <pre className="whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs">{x.raw}</pre>}
      <pre className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-xs leading-relaxed">{t?.text || (['PENDING', 'RUNNING'].includes(d.ocrStatus) ? 'Распознаётся: скан - около 10 секунд на страницу, запись - примерно в 5-10 раз быстрее её длительности...' : 'Текста нет')}</pre>
    </div>
  );
}
